// Notification Integration Service for TradeMate Pro
// Orchestrates in-app, email, and SMS notifications

import NotificationGenerator from './NotificationGenerator';
import NotificationTemplateService from './NotificationTemplateService';
import NotificationsService from './NotificationsService';
import settingsService from './SettingsService';
import smsNumberService from './SmsNumberService';

class NotificationIntegrationService {
  constructor() {
    this.processingQueue = new Map();
  }

  // Main notification dispatcher
  async sendNotification(companyId, eventType, data) {
    try {
      // Prevent duplicate processing
      const key = `${companyId}-${eventType}-${data.id || Date.now()}`;
      if (this.processingQueue.has(key)) {
        return this.processingQueue.get(key);
      }

      const promise = this._processNotification(companyId, eventType, data);
      this.processingQueue.set(key, promise);
      
      // Clean up after processing
      setTimeout(() => this.processingQueue.delete(key), 5000);
      
      return await promise;
    } catch (error) {
      console.error('Notification processing failed:', error);
      return { success: false, error: error.message };
    }
  }

  async _processNotification(companyId, eventType, data) {
    const results = {
      inApp: null,
      email: null,
      sms: null
    };

    // Get notification settings
    const settings = await settingsService.getSettings(companyId);
    
    // Process in-app notification. The customer appointment emails (booked, rescheduled,
    // cancelled, recurring series) are sent from these same generators, and switching the TEAM's
    // in-app notifications off must not stop them — an appointment confirmation is transactional
    // (PRODUCT_DECISIONS 2026-08-31; Flow 9 build decisions 2026-11-02). The generators skip only
    // their in-app row when the switch is off (inAppEnabled below).
    if (settings?.in_app_notifications_enabled !== false
      || NotificationIntegrationService.CUSTOMER_APPOINTMENT_EMAIL_EVENTS.includes(eventType)) {
      results.inApp = await this._processInAppNotification(companyId, eventType, {
        ...data, inAppEnabled: settings?.in_app_notifications_enabled !== false
      });
    }

    // Event flags gate the OUTBOUND channels only. In-app above is the contractor's own
    // activity feed — "notify customers about quotes" has nothing to say about whether the
    // office sees that a quote was approved.
    const eventMuted = this._eventMuted(settings, eventType);

    // Process email notification
    if (settings?.email_notifications_enabled === true && !eventMuted) {
      results.email = await this._processEmailNotification(companyId, eventType, data);
    }

    // Process SMS notification — sendability (live number AND preference) is owned by
    // SmsNumberService.canSendSms (#4784), not re-derived from settings here.
    if (!eventMuted && (await smsNumberService.canSendSms(companyId))) {
      results.sms = await this._processSmsNotification(companyId, eventType, data);
    }

    return results;
  }

  async _processInAppNotification(companyId, eventType, data) {
    try {
      switch (eventType) {
        case 'INVENTORY_LOW':
          return await NotificationGenerator.lowInventory(companyId, data.item, data.totals);
        
        case 'PTO_SUBMITTED':
          return await NotificationGenerator.ptoSubmitted(companyId, data.request);
        
        case 'INVOICE_OVERDUE':
          return await NotificationGenerator.invoiceOverdue(companyId, data.invoice);
        
        case 'QUOTE_EXPIRED':
          return await NotificationGenerator.quoteExpired(companyId, data.workOrder);
        
        case 'WORK_ORDER_CREATED':
          return await NotificationGenerator.workOrderCreated(companyId, data.workOrder, data.customer);
        
        case 'WORK_ORDER_STATUS_CHANGED':
          return await NotificationGenerator.workOrderStatusChanged(companyId, data.workOrder, data.oldStatus, data.newStatus);
        
        case 'JOB_SCHEDULED':
          return await NotificationGenerator.jobScheduled(companyId, data.job, data.employee, data.previous, {
            emailCustomer: data.emailCustomer !== false,
            inApp: data.inAppEnabled !== false
          });

        case 'JOB_SERIES_EMAIL':
          return await NotificationGenerator.jobSeriesEmail(companyId, data.kind, data.visits);

        case 'JOB_CANCELLED':
          return await NotificationGenerator.jobCancelled(companyId, data.job, data.previous);

        case 'JOB_COMPLETED':
          return await NotificationGenerator.jobCompleted(companyId, data.job, data.employee);
        
        case 'TIMESHEET_SUBMITTED':
          return await NotificationGenerator.timesheetSubmitted(companyId, data.timesheet, data.employee);
        
        case 'TIMESHEET_STATUS_CHANGED':
          return await NotificationGenerator.timesheetStatusChanged(companyId, data.timesheet, data.employee, data.status, data.approver);
        
        case 'EXPENSE_SUBMITTED':
          return await NotificationGenerator.expenseSubmitted(companyId, data.expense, data.employee);
        
        case 'EXPENSE_STATUS_CHANGED':
          return await NotificationGenerator.expenseStatusChanged(companyId, data.expense, data.employee, data.status, data.approver);
        
        case 'PURCHASE_ORDER_CREATED':
          return await NotificationGenerator.purchaseOrderCreated(companyId, data.po, data.vendor);
        
        case 'PURCHASE_ORDER_APPROVED':
          return await NotificationGenerator.purchaseOrderApproved(companyId, data.po, data.approver);
        
        case 'PAYMENT_RECEIVED':
          return await NotificationGenerator.paymentReceived(companyId, data.payment, data.invoice, data.customer);
        
        case 'PAYMENT_FAILED':
          return await NotificationGenerator.paymentFailed(companyId, data.payment, data.invoice, data.customer, data.reason);
        
        case 'CUSTOMER_CREATED':
          return await NotificationGenerator.customerCreated(companyId, data.customer, data.createdBy);
        
        case 'EMPLOYEE_CREATED':
          return await NotificationGenerator.employeeCreated(companyId, data.employee, data.createdBy);
        
        case 'SYSTEM_BACKUP_COMPLETED':
          return await NotificationGenerator.systemBackupCompleted(companyId, data.backupInfo);
        
        case 'INTEGRATION_ERROR':
          return await NotificationGenerator.integrationError(companyId, data.integration, data.error);
        
        case 'APPOINTMENT_REMINDER':
          return await NotificationGenerator.appointmentReminder(companyId, data.appointment, data.customer, data.employee);
        
        default:
          console.warn('Unknown notification event type:', eventType);
          return null;
      }
    } catch (error) {
      console.error('In-app notification failed:', error);
      return null;
    }
  }

  /**
   * Event types a CUSTOMER may decline (PRODUCT_DECISIONS.md, 2026-08-31).
   *
   * Reminders only. A quote, an invoice, a receipt and a payment confirmation are the
   * business relationship, not a courtesy — they are transactional and never customer-
   * optional, which is both the legal position (CAN-SPAM exempts transactional/relationship
   * mail from opt-out) and what Jobber, HouseCall Pro and ServiceTitan do.
   *
   * INVOICE_OVERDUE is deliberately NOT here: a dunning notice is a demand for money owed,
   * not a courtesy, and letting a customer mute it would make the debt quieter rather than
   * smaller.
   */
  static CUSTOMER_OPTOUTABLE_EVENTS = ['APPOINTMENT_REMINDER'];

  // Events whose generator emails the customer about their appointment (see _processNotification).
  static CUSTOMER_APPOINTMENT_EMAIL_EVENTS = ['JOB_SCHEDULED', 'JOB_SERIES_EMAIL', 'JOB_CANCELLED'];

  /**
   * Which company setting governs which AUTOMATED event.
   *
   * The settings split on two axes and were being collapsed into one:
   *   CHANNEL — email_notifications_enabled / sms_notifications_enabled ("how")
   *   EVENT   — send_quote_notifications / send_invoice_notifications ("what about")
   * A notification needs BOTH. `send_quote_notifications` was previously ANDed into a
   * generic email gate, so it muted job emails and had no effect on quotes at all.
   *
   * These govern AUTOMATED notifications only, never a Send button the contractor pressed
   * — a direct instruction is not a notification (PRODUCT_DECISIONS, 2026-08-31). That is
   * why QuoteSendingService and InvoiceSendingService call send-quote-email directly and
   * are deliberately outside this map.
   *
   * PAYMENT_RECEIVED sits under the invoice flag because a receipt is the billing
   * conversation. APPOINTMENT_REMINDER has no company event flag: it is governed by the
   * per-customer opt-out, which is the correct grain for it.
   */
  static EVENT_SETTING_KEYS = {
    QUOTE_SENT: 'send_quote_notifications',
    QUOTE_APPROVED: 'send_quote_notifications',
    QUOTE_REJECTED: 'send_quote_notifications',
    QUOTE_EXPIRED: 'send_quote_notifications',
    INVOICE_OVERDUE: 'send_invoice_notifications',
    PAYMENT_RECEIVED: 'send_invoice_notifications',
    PAYMENT_FAILED: 'send_invoice_notifications'
  };

  /**
   * True when the company has switched this event's notifications off.
   * `=== false` so an unset column (or a failed settings read) leaves notifications ON,
   * which is the current behaviour for every company.
   */
  _eventMuted(settings, eventType) {
    const key = NotificationIntegrationService.EVENT_SETTING_KEYS[eventType];
    if (!key) return false;
    return settings?.[key] === false;
  }

  /**
   * True when this customer has declined this notification.
   *
   * Applies ONLY to the customer's own copy — an employee recipient is staff, and staff
   * notifications are not governed by a customer's preference.
   */
  _customerDeclined(eventType, data) {
    if (!NotificationIntegrationService.CUSTOMER_OPTOUTABLE_EVENTS.includes(eventType)) return false;
    return data?.customer?.reminders_opt_out === true;
  }

  async _processEmailNotification(companyId, eventType, data) {
    try {
      const emailMapping = {
        'QUOTE_SENT': 'QUOTE_SENT',
        'QUOTE_APPROVED': 'QUOTE_APPROVED',
        'APPOINTMENT_REMINDER': 'APPOINTMENT_REMINDER',
        'INVOICE_OVERDUE': 'INVOICE_OVERDUE',
        'PAYMENT_RECEIVED': 'PAYMENT_RECEIVED',
        'WORK_ORDER_COMPLETED': 'WORK_COMPLETED'
      };

      const templateType = emailMapping[eventType];
      if (!templateType) return null;

      const recipient = data.customer?.email || data.employee?.email;
      if (!recipient) return null;
      // Suppress only the CUSTOMER's own copy. If the recipient resolved to the employee,
      // this is a staff notification and the customer's preference does not govern it.
      if (recipient === data.customer?.email && this._customerDeclined(eventType, data)) return null;

      return await NotificationTemplateService.sendEmail(companyId, templateType, data, recipient);
    } catch (error) {
      console.error('Email notification failed:', error);
      return null;
    }
  }

  async _processSmsNotification(companyId, eventType, data) {
    try {
      const smsMapping = {
        'APPOINTMENT_REMINDER': 'APPOINTMENT_REMINDER',
        'QUOTE_APPROVED': 'QUOTE_APPROVED',
        'URGENT_UPDATE': 'URGENT_UPDATE'
      };

      const templateType = smsMapping[eventType];
      if (!templateType) return null;

      const recipient = data.customer?.phone || data.employee?.phone;
      if (!recipient) return null;
      // Same rule on the text channel. This is IN ADDITION to the hard consent gate in
      // send-sms (has_sms_consent), which is a legal requirement and is not weakened here —
      // a customer with no SMS consent is never texted regardless of this preference.
      if (recipient === data.customer?.phone && this._customerDeclined(eventType, data)) return null;

      return await NotificationTemplateService.sendSms(companyId, templateType, data, recipient);
    } catch (error) {
      console.error('SMS notification failed:', error);
      return null;
    }
  }

  // Convenience methods for common events
  async notifyInventoryLow(companyId, item, totals) {
    return this.sendNotification(companyId, 'INVENTORY_LOW', { item, totals });
  }

  async notifyWorkOrderCreated(companyId, workOrder, customer) {
    return this.sendNotification(companyId, 'WORK_ORDER_CREATED', { workOrder, customer });
  }

  // previous = { start, end } when the booking moved an already-booked visit (a reschedule).
  // { emailCustomer: false } = the technician's in-app notification only; the customer is told
  // in one series email (notifyJobSeries) for a recurring plan's visits (ruling 2026-10-25 E).
  async notifyJobScheduled(companyId, job, employee, previous = null, { emailCustomer = true } = {}) {
    return this.sendNotification(companyId, 'JOB_SCHEDULED', { job, employee, previous, emailCustomer });
  }

  // One customer email for several of a recurring plan's visits (ruling 2026-10-25 E).
  // kind = 'series_scheduled' | 'series_updated' | 'series_cancelled'; visits = [{ id, start, end }].
  async notifyJobSeries(companyId, kind, visits) {
    return this.sendNotification(companyId, 'JOB_SERIES_EMAIL', { id: `${kind}-${visits?.[0]?.id}`, kind, visits });
  }

  // previous = { start, end } of the visit that was cancelled.
  async notifyJobCancelled(companyId, job, previous) {
    return this.sendNotification(companyId, 'JOB_CANCELLED', { id: job?.id, job, previous });
  }

  async notifyJobCompleted(companyId, job, employee) {
    return this.sendNotification(companyId, 'JOB_COMPLETED', { job, employee });
  }

  async notifyTimesheetSubmitted(companyId, timesheet, employee) {
    return this.sendNotification(companyId, 'TIMESHEET_SUBMITTED', { timesheet, employee });
  }

  async notifyExpenseSubmitted(companyId, expense, employee) {
    return this.sendNotification(companyId, 'EXPENSE_SUBMITTED', { expense, employee });
  }

  async notifyPaymentReceived(companyId, payment, invoice, customer) {
    return this.sendNotification(companyId, 'PAYMENT_RECEIVED', { payment, invoice, customer });
  }

  async notifyQuoteApproved(companyId, quote, customer) {
    return this.sendNotification(companyId, 'QUOTE_APPROVED', { quote, customer });
  }

  async notifyQuoteRejected(companyId, quote, customer) {
    return this.sendNotification(companyId, 'QUOTE_REJECTED', { quote, customer });
  }

  async notifyQuoteChangesRequested(companyId, quote, customer, message) {
    return this.sendNotification(companyId, 'QUOTE_CHANGES_REQUESTED', { quote, customer, message });
  }

  async notifyAppointmentReminder(companyId, appointment, customer, employee) {
    return this.sendNotification(companyId, 'APPOINTMENT_REMINDER', { appointment, customer, employee });
  }

  async notifyInvoiceOverdue(companyId, invoice, customer) {
    return this.sendNotification(companyId, 'INVOICE_OVERDUE', { invoice, customer });
  }

  // Batch notification processing
  async processBatch(companyId, notifications) {
    const results = [];
    
    for (const notification of notifications) {
      try {
        const result = await this.sendNotification(companyId, notification.eventType, notification.data);
        results.push({ ...notification, result });
      } catch (error) {
        results.push({ ...notification, error: error.message });
      }
    }
    
    return results;
  }

  // Get notification statistics
  async getNotificationStats(companyId, timeframe = '24h') {
    try {
      const hours = timeframe === '24h' ? 24 : timeframe === '7d' ? 168 : 720; // 30d
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      
      const notifications = await NotificationsService.getNotifications(companyId, null, {
        since,
        limit: 1000
      });

      const stats = {
        total: notifications.length,
        unread: notifications.filter(n => !n.is_read).length,
        byType: {},
        bySeverity: {}
      };

      notifications.forEach(n => {
        stats.byType[n.type] = (stats.byType[n.type] || 0) + 1;
        stats.bySeverity[n.severity] = (stats.bySeverity[n.severity] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Failed to get notification stats:', error);
      return { total: 0, unread: 0, byType: {}, bySeverity: {} };
    }
  }

  // Clean up old notifications
  async cleanupOldNotifications(companyId, olderThanDays = 30) {
    try {
      const cutoff = new Date(Date.now() - olderThanDays * 24 * 60 * 60 * 1000).toISOString();
      
      // This would need to be implemented in NotificationsService
      console.log(`Would clean up notifications older than ${cutoff} for company ${companyId}`);
      
      return { success: true, message: `Cleanup scheduled for notifications older than ${olderThanDays} days` };
    } catch (error) {
      console.error('Notification cleanup failed:', error);
      return { success: false, error: error.message };
    }
  }
}

/**
 * What happened to the customer's appointment email (booking, reschedule, cancel), from a
 * notifyJobScheduled / notifyJobCancelled result. The contractor is always told (PRODUCT_DECISIONS
 * 2026-10-21 and 2026-10-24). null = no email was attempted (emailCustomer: false). The team's
 * in-app switch no longer skips the customer email (Flow 9 Stage 5, 2026-11-04).
 */
export const customerEmailOutcome = (notifyResult) => {
  if (notifyResult && notifyResult.success === false) {
    return { sent: false, reason: notifyResult.error };
  }
  const result = notifyResult?.inApp?.customerEmail;
  if (!result) return null;
  return result.success ? { sent: true } : { sent: false, reason: result.error?.message || result.error };
};

const notificationIntegrationService = new NotificationIntegrationService();
export default notificationIntegrationService;
